import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2, TriangleAlert } from 'lucide-react'
import {
  allocateShipping, calcPurchaseTotals, formatMoney, landedUnitCost, purchaseSchema,
  toMajor, toMinor, weightedAverageCost,
} from '@najah/core'
import {
  Alert, Button, Dialog, Input, Select, Table, TableWrapper, Tbody, Td, Th, Thead, Tr,
  Textarea, EmptyState, useToast,
} from '@najah/ui'
import { productsApi } from '../../lib/catalogApi'
import { purchasesApi, suppliersApi } from '../../lib/purchasingApi'
import { useFormErrors } from '../../lib/useFormErrors'

interface Line {
  key: string
  productId: string
  nameAr: string
  sku: string
  currentQty: number
  currentCost: number
  unitCost: string
  quantity: string
  discount: string
  newRetailPrice: string
}

export function PurchaseFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()

  const [supplierId, setSupplierId] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [shipping, setShipping] = useState('')
  const [note, setNote] = useState('')
  const [status, setStatus] = useState<'RECEIVED' | 'DRAFT'>('RECEIVED')
  const [lines, setLines] = useState<Line[]>([])
  const [term, setTerm] = useState('')

  useEffect(() => {
    if (!open) return
    clear()
    setSupplierId('')
    setInvoiceNumber('')
    setInvoiceDate('')
    setShipping('')
    setNote('')
    setStatus('RECEIVED')
    setLines([])
    setTerm('')
  }, [open, clear])

  const { data: suppliers } = useQuery({ queryKey: ['suppliers', 'options'], queryFn: suppliersApi.options, enabled: open })

  const { data: results } = useQuery({
    queryKey: ['products', 'purchase-search', term],
    queryFn: () => productsApi.list({ search: term, pageSize: 10, isActive: 'yes' }),
    enabled: open && term.trim().length >= 2,
  })

  function addLine(p: { id: string; nameAr: string; sku: string; quantity: number; costPrice: number; retailPrice: number }) {
    setLines((prev) => {
      if (prev.some((l) => l.productId === p.id)) return prev
      return [
        ...prev,
        {
          key: crypto.randomUUID(),
          productId: p.id,
          nameAr: p.nameAr,
          sku: p.sku,
          currentQty: p.quantity,
          currentCost: p.costPrice,
          unitCost: String(toMajor(p.costPrice)),
          quantity: '1',
          discount: '',
          newRetailPrice: '',
        },
      ]
    })
    setTerm('')
  }

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const items = useMemo(
    () =>
      lines.map((l) => ({
        productId: l.productId,
        unitCost: toMinor(Number(l.unitCost) || 0),
        quantity: Number(l.quantity) || 0,
        discount: toMinor(Number(l.discount) || 0),
        taxRate: 0,
        newRetailPrice: l.newRetailPrice ? toMinor(Number(l.newRetailPrice)) : null,
      })),
    [lines],
  )

  const shippingMinor = toMinor(Number(shipping) || 0)
  const totals = useMemo(() => calcPurchaseTotals(items, shippingMinor, 0), [items, shippingMinor])

  /**
   * معاينة التكلفة الناتجة قبل الحفظ — الرقم الذي ستُحسب عليه الأرباح لاحقاً.
   * نستعمل نفس دوال @najah/core التي يستعملها الخادم، فالمعاينة ليست تقديراً.
   */
  const preview = useMemo(() => {
    const lineValues = items.map((i) => i.unitCost * i.quantity - i.discount)
    const shares = allocateShipping(lineValues, shippingMinor)
    return items.map((item, index) => {
      const line = lines[index]!
      const landed = landedUnitCost(item.unitCost, item.quantity, item.discount, shares[index] ?? 0)
      const newCost = weightedAverageCost(line.currentQty, line.currentCost, item.quantity, landed)
      return { landed, newCost, shippingShare: shares[index] ?? 0 }
    })
  }, [items, lines, shippingMinor])

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = validate(purchaseSchema, {
        supplierId,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: invoiceDate || '',
        items,
        shippingCost: shippingMinor,
        discountTotal: 0,
        note: note.trim(),
        status,
      })
      if (!parsed) throw new Error('VALIDATION')
      return purchasesApi.create(parsed)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] })
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success(
        res.status === 'RECEIVED' ? 'سُجّلت الفاتورة ودخلت البضاعة' : 'حُفظت المسوّدة',
        `${res.reference} — ${formatMoney(res.total)}`,
      )
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ الفاتورة')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title="فاتورة شراء جديدة"
      description="أدخل أصناف الفاتورة كما وردت من المورّد"
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button
            variant="secondary"
            onClick={() => { setStatus('DRAFT'); mutation.mutate() }}
            loading={mutation.isPending && status === 'DRAFT'}
            disabled={lines.length === 0}
          >
            حفظ كمسوّدة
          </Button>
          <Button
            onClick={() => { setStatus('RECEIVED'); mutation.mutate() }}
            loading={mutation.isPending && status === 'RECEIVED'}
            disabled={lines.length === 0}
          >
            استلام وإدخال المخزون
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="المورّد" required
            value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
            options={(suppliers ?? []).map((s) => ({ value: s.id, label: `${s.nameAr} (${s.code})` }))}
            placeholder="اختر مورّداً"
            error={errors.supplierId}
          />
          <Input
            label="رقم فاتورة المورّد" ltr
            value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
            error={errors.invoiceNumber}
          />
          <Input
            label="تاريخ الفاتورة" type="date" ltr
            value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
          />
        </div>

        {/* ── إضافة الأصناف ── */}
        <div className="space-y-2">
          <Input
            label="إضافة صنف"
            placeholder="ابحث بالاسم أو الرمز أو الباركود…"
            value={term} onChange={(e) => setTerm(e.target.value)}
            startIcon={<Search className="size-4" />}
          />
          {term.trim().length >= 2 && results && results.items.length > 0 && (
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-surface">
              {results.items.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => addLine(p)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-start text-sm transition-colors hover:bg-surface-hover"
                  >
                    <Plus className="size-3.5 shrink-0 text-fg-subtle" />
                    <span className="min-w-0 flex-1 truncate">{p.nameAr}</span>
                    <span className="font-mono text-xs text-fg-subtle" dir="ltr">{p.sku}</span>
                    <span className="text-xs text-fg-muted" dir="ltr">
                      الرصيد {p.quantity} · التكلفة {formatMoney(p.costPrice)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errors.items && <p className="text-xs text-danger">{errors.items}</p>}
        </div>

        {/* ── الأصناف ── */}
        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>الصنف</Th>
                <Th align="center">الكمية</Th>
                <Th align="center">تكلفة الوحدة</Th>
                <Th align="center">خصم السطر</Th>
                <Th align="center">سعر بيع جديد</Th>
                <Th align="end">الإجمالي</Th>
                <Th align="center"></Th>
              </tr>
            </Thead>
            <Tbody>
              {lines.map((line, index) => {
                const item = items[index]!
                const p = preview[index]!
                const lineTotal = item.unitCost * item.quantity - item.discount
                return (
                  <Tr key={line.key}>
                    <Td>
                      <p className="font-medium text-fg">{line.nameAr}</p>
                      <p className="font-mono text-xs text-fg-subtle" dir="ltr">{line.sku}</p>
                      {item.quantity > 0 && (
                        <p className="mt-1 text-[11px] text-fg-subtle">
                          التكلفة الواصلة <span className="font-medium text-fg-muted" dir="ltr">{formatMoney(p.landed)}</span>
                          {' → '}
                          المتوسّط الجديد <span className="font-medium text-primary" dir="ltr">{formatMoney(p.newCost)}</span>
                        </p>
                      )}
                    </Td>
                    <Td align="center">
                      <input
                        type="number" min="1" dir="ltr"
                        value={line.quantity}
                        onChange={(e) => setLine(line.key, { quantity: e.target.value })}
                        className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-center text-sm tabular-nums"
                      />
                    </Td>
                    <Td align="center">
                      <input
                        type="number" min="0" step="0.01" dir="ltr"
                        value={line.unitCost}
                        onChange={(e) => setLine(line.key, { unitCost: e.target.value })}
                        className="w-24 rounded-lg border border-border bg-surface px-2 py-1.5 text-center text-sm tabular-nums"
                      />
                    </Td>
                    <Td align="center">
                      <input
                        type="number" min="0" step="0.01" dir="ltr" placeholder="0"
                        value={line.discount}
                        onChange={(e) => setLine(line.key, { discount: e.target.value })}
                        className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-center text-sm tabular-nums"
                      />
                    </Td>
                    <Td align="center">
                      <input
                        type="number" min="0" step="0.01" dir="ltr" placeholder="بلا تغيير"
                        value={line.newRetailPrice}
                        onChange={(e) => setLine(line.key, { newRetailPrice: e.target.value })}
                        className="w-24 rounded-lg border border-border bg-surface px-2 py-1.5 text-center text-sm tabular-nums"
                      />
                    </Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(lineTotal)}</Td>
                    <Td align="center">
                      <button
                        type="button"
                        onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                        className="rounded p-1 text-fg-subtle transition-colors hover:bg-danger-subtle hover:text-danger"
                        aria-label="حذف السطر"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
          {lines.length === 0 && (
            <EmptyState icon={<Plus className="size-8" />} title="لم تُضف أصناف بعد" description="ابحث أعلاه وأضف أصناف الفاتورة" />
          )}
        </TableWrapper>

        {/* ── الإجماليات ── */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-4">
            <Input
              label="مصاريف نقل وجمركة" type="number" min="0" step="0.01" ltr
              value={shipping} onChange={(e) => setShipping(e.target.value)}
              error={errors.shippingCost}
              hint="تُوزَّع على الأصناف بنسبة قيمتها"
            />
            <Textarea label="ملاحظات" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="space-y-1 rounded-xl border border-border bg-bg-subtle p-4 text-sm">
            <div className="flex justify-between text-fg-muted">
              <span>مجموع الأصناف</span>
              <span dir="ltr">{formatMoney(totals.subtotal)}</span>
            </div>
            {totals.itemDiscount > 0 && (
              <div className="flex justify-between text-fg-muted">
                <span>الخصم</span>
                <span dir="ltr">−{formatMoney(totals.itemDiscount)}</span>
              </div>
            )}
            {shippingMinor > 0 && (
              <div className="flex justify-between text-fg-muted">
                <span>النقل</span>
                <span dir="ltr">{formatMoney(shippingMinor)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-2 text-lg font-bold text-fg">
              <span>الإجمالي</span>
              <span dir="ltr">{formatMoney(totals.total)}</span>
            </div>
          </div>
        </div>

        {shippingMinor > 0 && lines.length > 0 && (
          <Alert tone="info">
            <span className="inline-flex items-center gap-1.5">
              <TriangleAlert className="size-3.5" />
              مصاريف النقل مُوزَّعة على الأصناف بنسبة قيمة كل سطر — فتكون تكلفة الوحدة هي التكلفة
              الحقيقية الواصلة، وتبقى نسب الربح صادقة.
            </span>
          </Alert>
        )}
      </div>
    </Dialog>
  )
}
